import React from 'react';
import { SiteHeader, SiteFooter } from './Layout';
import './App.css';

function TermsOfService() {
  return (
    <div className="page">
      <SiteHeader />

      <main className="legal container">
        <h1 className="legal-title">Terms of Service</h1>
        <p className="legal-date">Last updated: August 28, 2025</p>

        <div className="policy-section">
          <h2>1. Acceptance of Terms</h2>
          <p>
            By accessing and using the services provided by Code That Down LLC ("we," "our," or "us"), 
            you accept and agree to be bound by the terms and provision of this agreement. 
            If you do not agree to abide by the above, please do not use this service.
          </p>
        </div>

        <div className="policy-section">
          <h2>2. Description of Service</h2>
          <p>
            Code That Down LLC provides custom software development services and distributes applications
            through various platforms, including but not limited to:
          </p>
          <ul>
            <li>Game development for mobile and desktop platforms</li>
            <li>Productivity application development</li>
            <li>Custom software solutions</li>
            <li>Mobile applications distributed through app stores (Apple App Store, Google Play Store, etc.)</li>
            <li>Web-based applications and services</li>
            <li>Consulting and technical advisory services</li>
          </ul>
          <p>
            Some of our services may be accessed through third-party platforms such as mobile app stores,
            which have their own terms of service that also apply to your use of our applications.
          </p>
        </div>

        <div className="policy-section">
          <h2>3. App Store Applications</h2>
          <p>
            For applications distributed through third-party app stores (Apple App Store, Google Play Store,
            Microsoft Store, etc.), the following additional terms apply:
          </p>
          <ul>
            <li><strong>Third-Party Terms:</strong> Your use of our apps is also subject to the terms and conditions of the respective app store</li>
            <li><strong>App Store Policies:</strong> We comply with all applicable app store guidelines and policies</li>
            <li><strong>Updates:</strong> App updates may be delivered through the app store's update mechanism</li>
            <li><strong>Purchases:</strong> In-app purchases and subscriptions are processed by the app store platform</li>
            <li><strong>Refunds:</strong> Refund requests for app store purchases must be made through the respective app store</li>
            <li><strong>Age Restrictions:</strong> Some apps may have age restrictions as determined by app store ratings</li>
            <li><strong>Geographic Availability:</strong> App availability may vary by region based on app store policies</li>
          </ul>
          <p>
            <strong>Apple App Store:</strong> If you download our app from the Apple App Store, you acknowledge
            that these terms are between you and Code That Down LLC, not Apple. Apple is not responsible for
            our app or any claims relating to it.
          </p>
          <p>
            <strong>Google Play Store:</strong> If you download our app from Google Play, you acknowledge that
            these terms are between you and Code That Down LLC, not Google. Google is not responsible for
            our app or any claims relating to it.
          </p>
        </div>

        <div className="policy-section">
          <h2>4. User Responsibilities</h2>
          <p>As a user of our services and applications, you agree to:</p>
          <ul>
            <li>Provide accurate and complete information when requested</li>
            <li>Maintain the confidentiality of any login credentials</li>
            <li>Use our services and applications only for lawful purposes</li>
            <li>Not attempt to gain unauthorized access to our systems or reverse engineer our apps</li>
            <li>Respect intellectual property rights</li>
            <li>Comply with all applicable laws, regulations, and app store policies</li>
            <li>Not use our applications to distribute malware, spam, or inappropriate content</li>
            <li>Respect other users and maintain appropriate conduct within our applications</li>
          </ul>
        </div>

        <div className="policy-section">
          <h2>5. Intellectual Property Rights</h2>
          <p>
            <strong>Custom Development Projects:</strong> Unless otherwise specified in a separate written agreement,
            all intellectual property rights in custom work products created by Code That Down LLC shall be
            owned by the client upon full payment of all fees.
          </p>
          <p>
            <strong>Our Published Applications:</strong> All intellectual property rights in applications we
            develop and publish (including games and productivity apps distributed through app stores)
            remain the exclusive property of Code That Down LLC.
          </p>
          <p>
            Code That Down LLC retains the right to:
          </p>
          <ul>
            <li>Use general knowledge, skills, and experience gained during projects</li>
            <li>Reference projects in portfolios and marketing materials (with client consent for custom work)</li>
            <li>Retain ownership of pre-existing intellectual property, tools, and frameworks</li>
            <li>Protect our published applications from unauthorized copying or distribution</li>
          </ul>
          <p>
            Users of our published applications receive a limited, non-exclusive, non-transferable license
            to use the application for personal or business purposes as intended.
          </p>
        </div>

        <div className="policy-section">
          <h2>6. Payment Terms and In-App Purchases</h2>
          <p>
            <strong>Custom Development Services:</strong> Payment terms will be specified in individual project agreements. Generally:
          </p>
          <ul>
            <li>Payment schedules will be agreed upon before project commencement</li>
            <li>Late payments may incur additional fees</li>
            <li>Refunds are subject to the terms of individual project agreements</li>
            <li>All fees are exclusive of applicable taxes</li>
          </ul>
          <p>
            <strong>App Store Applications:</strong> Some of our applications may offer in-app purchases, subscriptions, or premium features:
          </p>
          <ul>
            <li>All purchases are processed through the respective app store platform</li>
            <li>Prices are set in the app store and may vary by region</li>
            <li>Subscriptions automatically renew unless cancelled through your app store account</li>
            <li>Refund requests must be made through the app store (Apple App Store, Google Play, etc.)</li>
            <li>We do not have access to or control over app store payment processing</li>
            <li>Premium features are tied to your app store account and platform</li>
          </ul>
          <p>
            <strong>Free Applications:</strong> Some of our applications are provided free of charge but may
            include advertisements or optional premium features.
          </p>
        </div>

        <div className="policy-section">
          <h2>7. App Updates and Availability</h2>
          <p>
            <strong>Custom Development:</strong> Project deliverables, timelines, and acceptance criteria will be defined in
            individual project agreements. We strive to meet all agreed-upon deadlines,
            but delivery dates are estimates and may be subject to change due to unforeseen circumstances.
          </p>
          <p>
            <strong>Published Applications:</strong> We may update our applications from time to time to add features,
            fix bugs, or improve performance. Updates may be delivered through app stores or automatic update mechanisms.
          </p>
          <ul>
            <li>We reserve the right to modify or discontinue any application at any time</li>
            <li>App availability may vary by region or platform</li>
            <li>Some updates may require newer device operating systems</li>
            <li>We are not obligated to maintain backward compatibility with older versions</li>
          </ul>
        </div>

        <div className="policy-section">
          <h2>8. Limitation of Liability</h2>
          <p>
            To the maximum extent permitted by law, Code That Down LLC shall not be liable
            for any indirect, incidental, special, consequential, or punitive damages,
            including without limitation, loss of profits, data, use, goodwill, or other
            intangible losses, resulting from your use of our services or applications.
          </p>
          <p>
            For applications distributed through app stores, our liability is further limited
            by the terms and conditions of the respective app store platforms.
          </p>
        </div>

        <div className="policy-section">
          <h2>9. Warranty Disclaimer</h2>
          <p>
            Our services and applications are provided "as is" without warranty of any kind, either express
            or implied, including but not limited to the implied warranties of merchantability,
            fitness for a particular purpose, or non-infringement.
          </p>
          <p>
            We do not warrant that our applications will be uninterrupted, error-free, or compatible
            with all devices and operating systems.
          </p>
        </div>

        <div className="policy-section">
          <h2>10. Confidentiality and Data Collection</h2>
          <p>
            We respect the confidentiality of our clients' information and will not disclose
            confidential information to third parties without proper authorization, except as
            required by law or as necessary to provide our services.
          </p>
          <p>
            For our published applications, we may collect usage data and analytics to improve
            our services. Please refer to our Privacy Policy for detailed information about
            data collection and usage.
          </p>
        </div>

        <div className="policy-section">
          <h2>11. Termination and Account Suspension</h2>
          <p>
            <strong>Custom Development:</strong> Either party may terminate a project agreement with written notice. Upon termination:
          </p>
          <ul>
            <li>Payment will be due for all work completed up to the termination date</li>
            <li>Each party will return or destroy confidential information</li>
            <li>Surviving provisions (such as confidentiality and intellectual property) will remain in effect</li>
          </ul>
          <p>
            <strong>Application Usage:</strong> We reserve the right to suspend or terminate your access to our
            applications if you violate these terms, engage in fraudulent activity, or misuse our services.
          </p>
        </div>

        <div className="policy-section">
          <h2>12. Governing Law and Dispute Resolution</h2>
          <p>
            These terms shall be governed by and construed in accordance with the laws of
            the State of Georgia, United States, without regard to its conflict of law provisions.
          </p>
          <p>
            Any disputes arising from the use of our applications distributed through app stores
            may also be subject to the dispute resolution mechanisms provided by those platforms.
          </p>
        </div>

        <div className="policy-section">
          <h2>13. Changes to Terms</h2>
          <p>
            We reserve the right to modify these terms at any time. We will notify users of
            any material changes by posting the new Terms of Service on this page and updating
            the "Last updated" date at the top of these terms.
          </p>
          <p>
            For applications distributed through app stores, we may also notify users through
            app update notifications or in-app messages when significant changes occur.
          </p>
        </div>

        <div className="policy-section">
          <h2>14. Contact Information</h2>
          <p>
            If you have any questions about these Terms of Service, please contact us at:
          </p>
          <div className="contact-info">
            <p><strong>Code That Down LLC</strong></p>
            <p>Email: <a href="mailto:contact@codethatdown.com">contact@codethatdown.com</a></p>
            <p>LinkedIn: <a href="https://www.linkedin.com/company/code-that-down" target="_blank" rel="noopener noreferrer">Code That Down</a></p>
          </div>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}

export default TermsOfService;
